import { defineStore } from "pinia";
import { ref, computed } from "vue";
import AuthService from "../services/auth.service";
import type { User } from "../types";

// API Login Response tipi
interface ApiLoginResponse {
  success?: boolean;
  accessToken: string;
  refreshToken: string;
  user: User;
  message?: string;
}

export const useAuthStore = defineStore("auth", () => {
  // State
  const user = ref<User | null>(null);
  const accessToken = ref<string | null>(null);
  const refreshToken = ref<string | null>(null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  // Getters
  const isAuthenticated = computed(() => !!accessToken.value);
  const currentUser = computed(() => user.value);
  const userRole = computed(() => user.value?.role);

  // Actions
  async function login(
    email: string,
    password: string,
    authenticatorCode: string
  ) {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await AuthService.login(
        email,
        password,
        authenticatorCode
      );
      setAuthData(response);
      return response;
    } catch (err: any) {
      error.value = err.response?.data?.message || "Login failed";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function loginRequest(email: string, password: string) {
    isLoading.value = true;
    error.value = null;

    try {
      return await AuthService.loginRequest(email, password);
    } catch (err: any) {
      error.value = err.response?.data?.message || "Login request failed";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function registerStart(email: string, password: string) {
    isLoading.value = true;
    error.value = null;

    try {
      return await AuthService.registerStart(email, password);
    } catch (err: any) {
      error.value = err.response?.data?.message || "Registration failed";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function registerVerify(email: string, verificationCode: string) {
    isLoading.value = true;
    error.value = null;

    try {
      return await AuthService.registerVerify(email, verificationCode);
    } catch (err: any) {
      error.value = err.response?.data?.message || "Verification failed";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function register(registerData: any) {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await AuthService.register(registerData);
      setAuthData(response);
      return response;
    } catch (err: any) {
      error.value = err.response?.data?.message || "Registration failed";
      throw err;
    } finally {
      isLoading.value = false;
    }
  }

  async function refreshAuthToken() {
    if (!refreshToken.value) {
      throw new Error("No refresh token available");
    }

    try {
      const response = await AuthService.refreshToken(refreshToken.value);
      setAuthData(response);
      return response;
    } catch (err) {
      logout();
      throw err;
    }
  }

  function logout() {
    // localStorage'dan temizle
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('loginUser');
    
    user.value = null;
    accessToken.value = null;
    refreshToken.value = null;
    error.value = null;
  }

  function setAuthData(response: ApiLoginResponse) {
    // API response: { success, accessToken, refreshToken, user }
    // success field'ı varsa kontrol et, yoksa direkt kaydet
    if (response.success === false) {
      return; // success false ise kaydetme
    }
    
    if (response.accessToken && response.user) {
      user.value = response.user;
      accessToken.value = response.accessToken;
      refreshToken.value = response.refreshToken;
      
      // localStorage'a kaydet
      localStorage.setItem('accessToken', response.accessToken);
      localStorage.setItem('refreshToken', response.refreshToken);
      localStorage.setItem('loginUser', JSON.stringify(response.user));
    }
  }

  function loadFromStorage() {
    const storedAccessToken = localStorage.getItem('accessToken');
    const storedRefreshToken = localStorage.getItem('refreshToken');
    const storedUser = localStorage.getItem('loginUser');
    
    if (storedAccessToken && storedUser) {
      accessToken.value = storedAccessToken;
      refreshToken.value = storedRefreshToken;
      try {
        user.value = JSON.parse(storedUser);
      } catch (e) {
        console.error('Failed to parse stored user:', e);
      }
    }
  }

  // Initialize from localStorage
  loadFromStorage();

  return {
    // State
    user,
    accessToken,
    refreshToken,
    isLoading,
    error,
    // Getters
    isAuthenticated,
    currentUser,
    userRole,
    // Actions
    login,
    loginRequest,
    registerStart,
    registerVerify,
    register,
    refreshAuthToken,
    logout,
    loadFromStorage,
    setAuthData,
  };
});
